// src/components/Jobs/JobCard.js
import React from 'react';
import { Card, Spinner, Badge } from 'react-bootstrap';
import useImageWithAuth from '../../hooks/useImageWithAuth';
import { withCacheBust } from '../../utils/mediaUtils';
import './JobCard.css';

const JobCard = ({
      job,
      isRefreshing,
      onClick,
      selectionMode = false,
      isSelected = false,
      onToggleSelect,
      showOwnerBadge = true,
      showGroupBadges = false
}) => {
  // Prefer the rotated thumbnail if it exists, otherwise use the original thumbnail.
  // rotated_thumbnail has a stable filename that doesn't change on regeneration, so
  // a cache-busting version param is required to see the updated image after Save
  // Adjustments (same pattern as ProcessingResultsPage.js).
  const thumbnailField = job.rotated_thumbnail || job.thumbnail;
  const thumbnailUrl = withCacheBust(thumbnailField, job.user_modifications_saved_at);
  const [image, imageStatus, imageError] = useImageWithAuth('photo_processing', thumbnailUrl);

  // Sharing system props (same pattern as ExperimentCard)
  const isOwner = job.is_owner ?? true;
  const groups = job.shared_groups ?? [];

  const handleClick = (e) => {
        if (selectionMode) {
            e.preventDefault();
            onToggleSelect();
        } else {
            onClick();
        }
    };

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        onToggleSelect();
    };

  const getStatusVariant = () => {
        switch(job.status) {
            case 'completed': return 'success';
            case 'failed': return 'danger';
            case 'processing': return 'primary';
            default: return 'secondary';
        }
  };

  return (
    <Card
      onClick={handleClick}
      style={{ position: 'relative', cursor: 'pointer' }}
      className={`job-card h-100 ${selectionMode ? 'selection-mode' : ''} ${isSelected ? 'selected' : ''}`}
    >
      {/* Selection checkbox row (top of the card, shown only in selection mode) */}
      {selectionMode && (
                <div className="job-card__selection-row" onClick={handleCheckboxClick}>
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}} // Controlled by parent
                        className="form-check-input"
                    />
                </div>
      )}

      <Card.Header className="d-flex justify-content-between align-items-center">
           <div className="d-flex align-items-center gap-2">
             <Badge bg={getStatusVariant()}>
                  {job.status.toUpperCase()}
             </Badge>
             {showOwnerBadge && !isOwner && (
               <span className="job-card__shared-badge">
                 <i className="bi bi-share-fill me-1" />
                 Shared
               </span>
             )}
           </div>
           {isRefreshing && <Spinner animation="border" size="sm" />}
      </Card.Header>

      {/* Show image area if any thumbnail exists */}
      {thumbnailField ? (
        <>
          {imageStatus === 'loading' && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
              <Spinner animation="border" size="sm" />
            </div>
          )}

          {imageStatus === 'loaded' && image && (
            <Card.Img
              variant="top"
              src={image.src}
              style={{ objectFit: 'cover', height: '150px' }}
              alt={`Processing job ${job.id}`}
            />
          )}

          {imageStatus === 'failed' && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
              <span className="text-danger">Image load failed</span>
            </div>
          )}
        </>
      ) : (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
          <span>No Image</span>
        </div>
      )}

      <Card.Body>
        <Card.Title>{job.original_image_filename || 'Untitled'}</Card.Title>
        <Card.Text>
          <small className="text-muted">
            Created: {new Date(job.created_at).toLocaleString()}
          </small>
          <br />
          <strong>Type:</strong> {job.image_type_display}
          <br />
          <strong>Crop:</strong> {job.crop_type_display}
          <br />
          <strong>Orientation:</strong> {job.orientation_display}
          <br />
          <strong>Grid:</strong> {(() => {
            const dims = job.adjusted_grid_dimensions || job.grid_dimensions;
            return dims ? `${dims[0]} x ${dims[1]}` : 'N/A';
          })()}
        </Card.Text>
      </Card.Body>

      {/* Group chips (shown in All / Mine tabs) */}
      {showGroupBadges && groups.length > 0 && (
        <div className="job-card__groups">
          {groups.slice(0, 3).map(g => (
            <span key={g.id} className="job-card__group-chip" title={g.name}>
              <i className="bi bi-people-fill me-1" />
              {g.name}
            </span>
          ))}
          {groups.length > 3 && (
            <span className="job-card__group-chip job-card__group-chip--more">
              +{groups.length - 3}
            </span>
          )}
        </div>
      )}

      {job.status === 'failed' && job.error_message && (
           <Card.Footer className="text-danger small">
               Error: {job.error_message}
           </Card.Footer>
      )}

      {job.status === 'processing' && (
           <Card.Footer className="text-muted small">
               Processing since: {new Date(job.updated_at).toLocaleTimeString()}
           </Card.Footer>
      )}
    </Card>
  );
};

export default JobCard;